import { useState, useEffect, useCallback } from 'react';
import {
  ClipboardCheck,
  CheckCircle,
  XCircle,
  Clock,
  User,
  ChevronDown,
  ChevronUp,
  Loader2
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { getAuthHeaders } from '../../services/adminApi';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ============================================
// Review Item Card
// ============================================
const ReviewItem = ({ item, onApprove, onReject, expanded, onToggle }) => {
  const [pointsToAward, setPointsToAward] = useState(item.max_points || 10);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await onApprove(item.id, pointsToAward, notes);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await onReject(item.id, notes);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Card className={`transition-all ${expanded ? 'ring-2 ring-blue-200' : ''}`}>
      <CardContent className="p-4">
        {/* Header - Always Visible */}
        <button
          onClick={onToggle}
          className="w-full flex items-start justify-between text-left"
        >
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <User className="h-4 w-4 text-gray-400" />
              <span className="font-medium">{item.student_name}</span>
              <span className="text-gray-400">•</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">{item.quiz_title}</span>
            </div>
            <p className="text-sm text-gray-600 line-clamp-2">{item.question_text}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">
              {new Date(item.submitted_at).toLocaleDateString()}
            </span>
            {expanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </button>

        {/* Expanded Content */}
        {expanded && (
          <div className="mt-4 pt-4 border-t space-y-4">
            {/* Question & Expected */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Question</p>
                <p className="text-sm">{item.question_text}</p>
              </div>

              {item.expected_answer && (
                <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3">
                  <p className="text-xs font-medium text-green-700 uppercase mb-1">Expected Answer</p>
                  <p className="text-sm text-green-800">{item.expected_answer}</p>
                  {item.keywords?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {item.keywords.map((kw, i) => (
                        <span key={i} className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                          {kw}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Student's Answer */}
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
              <p className="text-xs font-medium text-blue-700 uppercase mb-1">Student's Answer</p>
              <p className="text-sm text-blue-900 whitespace-pre-wrap">{item.student_answer || '(No answer provided)'}</p>
            </div>

            {/* Points & Notes */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-500 uppercase mb-1 block">
                  Points to Award (max: {item.max_points})
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="range"
                    min="0"
                    max={item.max_points}
                    value={pointsToAward}
                    onChange={(e) => setPointsToAward(parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    min="0"
                    max={item.max_points}
                    value={pointsToAward}
                    onChange={(e) => setPointsToAward(Math.min(parseInt(e.target.value) || 0, item.max_points))}
                    className="w-20"
                  />
                </div>
              </div>

              <div className="flex-1">
                <label className="text-xs font-medium text-gray-500 uppercase mb-1 block">
                  Notes (optional)
                </label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Feedback for the student..."
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={handleReject}
                disabled={processing}
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                {processing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="h-4 w-4 mr-2" />
                )}
                Reject
              </Button>
              <Button
                onClick={handleApprove}
                disabled={processing}
                variant="adminPrimary"
              >
                {processing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Approve ({pointsToAward} pts)
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================
// Main Review Queue Component
// ============================================
const ReviewQueue = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('pending');

  const fetchItems = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/review-queue${filter !== 'all' ? `?status=${filter}` : ''}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Failed to fetch review queue:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleApprove = async (itemId, points, notes) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/review/${itemId}/approve`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            points_awarded: points,
            reviewer_notes: notes || undefined
          })
        }
      );

      if (response.ok) {
        // Remove from list or refresh
        setItems(prev => prev.filter(i => i.id !== itemId));
        setExpandedId(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to approve');
      }
    } catch (error) {
      console.error('Failed to approve:', error);
      alert('Failed to approve answer');
    }
  };

  const handleReject = async (itemId, notes) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/review/${itemId}/reject`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            reviewer_notes: notes || undefined
          })
        }
      );

      if (response.ok) {
        setItems(prev => prev.filter(i => i.id !== itemId));
        setExpandedId(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to reject');
      }
    } catch (error) {
      console.error('Failed to reject:', error);
      alert('Failed to reject answer');
    }
  };

  const handleBulkApprove = async () => {
    if (!window.confirm(`Approve all ${items.length} pending answers with full points?`)) {
      return;
    }

    setLoading(true);
    try {
      for (const item of items) {
        await handleApprove(item.id, item.max_points, '');
      }
    } finally {
      await fetchItems();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6 text-blue-600" />
            Review Queue
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
            Review and grade short answer quiz responses
          </p>
        </div>

        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="p-2 border rounded-lg bg-background text-foreground border-gray-300 dark:border-gray-600"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>

          {filter === 'pending' && items.length > 0 && (
            <Button
              variant="outline"
              onClick={handleBulkApprove}
              className="border-green-300 text-green-600 hover:bg-green-50"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Approve All ({items.length})
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{items.filter(i => i.status === 'pending').length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Pending</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{items.filter(i => i.status === 'approved').length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Approved</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <XCircle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{items.filter(i => i.status === 'rejected').length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Rejected</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Review Items */}
      {items.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
          <ClipboardCheck className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {filter === 'pending' ? 'No pending reviews' : 'No reviews found'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {filter === 'pending'
              ? 'All caught up! Short answer responses will appear here.'
              : 'Try a different filter to see more reviews.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(item => (
            <ReviewItem
              key={item.id}
              item={item}
              expanded={expandedId === item.id}
              onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewQueue;
